import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { BounceLoader } from 'react-spinners';
import {
  reduxForm,
  formValueSelector,
  getFormSyncErrors,
  getFormValues,
} from 'redux-form';
import {
  fetchAddresses,
  selectAddress,
  resetAutocomplete,
  completeRegistration,
  initCompleteRegistration,
} from '../../redux/actions';
import { gtagEvent } from '../../common/lib/analyticsEventManager';

import RequirementsInfoAccordion from './RequirementsInfoAccordion';
import RegisterForm from './RegisterForm';
import {
  focusValidationSummaryById,
  scrollToFirstErrorField,
  VALIDATION_SUMMARY_TO_FIELD_DELAY_MS,
} from '../../common/lib/scrollToFirstErrorField';
import { clearTimeoutRef, setComponentTimeout } from '../../common/lib/setComponentTimeout';
import { redirectToLogin } from '../../common/lib/redirectToLogin';
import {
  isClaimedByOtherTab,
  subscribeToRegistrationFlowClaim,
} from '../../common/lib/registrationFlowSession';

const formName = 'completeRegistration';

const formSelector = formValueSelector(formName);

import '../../../style/index.css';
import './complete-registration.css';

const mapStateToProps = (state) => ({
  addressInput: formSelector(state, 'addressAutocomplete'),
  address: formSelector(state, 'address'),
  addresses: state.registerReducer.addresses,
  addressAutocompleteIsFetching: state.registerReducer.addressAutocompleteIsFetching,
  addressErrorDetail: state.registerReducer.addressErrorDetail,
  errorDetail: state.registerReducer.errorDetail,
  registrationFailed: state.registerReducer.registrationFailed,
  registrationSuccess: state.registerReducer.registrationSuccess,
  statusCode: state.registerReducer.statusCode,
  formErrors: getFormSyncErrors(formName)(state),
  selectedAddress: state.registerReducer.selectedAddress,
  selectedAddressParsed: state.registerReducer.selectedAddressParsed,
  isFetching: state.registerReducer.isFetching,
  guardChecking: state.registerReducer.guardChecking,
  guardReady: state.registerReducer.guardReady,
  completeRegistrationFormValues: getFormValues(formName)(state),
});

const mapDispatchToProps = {
  fetchAddresses,
  selectAddress,
  resetAutocomplete,
  completeRegistration,
  initCompleteRegistration,
};

@connect(mapStateToProps, mapDispatchToProps)
class CompleteRegistrationView extends Component {
  static propTypes = {
    valid: PropTypes.bool,
    touch: PropTypes.func,
    formErrors: PropTypes.object,
    fetchAddresses: PropTypes.func,
    selectAddress: PropTypes.func,
    history: PropTypes.object,
    completeRegistration: PropTypes.func,
    initCompleteRegistration: PropTypes.func,
    completeRegistrationFormValues: PropTypes.object,
    guardChecking: PropTypes.bool,
    guardReady: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.handleRegisterSubmit = this.handleRegisterSubmit.bind(this);
    this.selectAddressFn = this.selectAddressFn.bind(this);
    this.handleRegistrationFlowClaim = this.handleRegistrationFlowClaim.bind(this);
    this.formRef = React.createRef();
    this.timeoutRef = React.createRef();
    this.unsubscribeFromRegistrationFlowClaim = null;
    this.state = {
      formSubmissionError: false,
      isSubmitting: false,
    };
  }

  handleRegistrationFlowClaim() {
    if (isClaimedByOtherTab()) {
      redirectToLogin(this.props.history);
    }
  }

  componentDidMount() {
    const { initCompleteRegistration, history } = this.props;
    this.unsubscribeFromRegistrationFlowClaim = subscribeToRegistrationFlowClaim(
      this.handleRegistrationFlowClaim,
    );
    if (isClaimedByOtherTab()) {
      redirectToLogin(history);
      return;
    }
    gtagEvent('DC-5139115/colle0/colle00-+standard');
    initCompleteRegistration(history.push);
  }

  componentWillUnmount() {
    clearTimeoutRef(this.timeoutRef);
    if (this.unsubscribeFromRegistrationFlowClaim) {
      this.unsubscribeFromRegistrationFlowClaim();
    }
  }

  componentDidUpdate(prevProps) {
    const { isFetching } = this.props;
    if (prevProps.isFetching && !isFetching && this.state.isSubmitting) {
      this.setState({ isSubmitting: false });
    }
  }

  handleRegisterSubmit(e) {
    e.preventDefault();
    const {
      valid,
      touch,
      formErrors,
      completeRegistration,
      history,
      completeRegistrationFormValues,
      selectedAddressParsed,
      isFetching,
    } = this.props;

    if (isFetching || this.state.isSubmitting) {
      return;
    }

    if (valid) {
      gtagEvent('DC-5139115/nylot0/nylre0+standard');
      this.setState({
        formSubmissionError: false,
        isSubmitting: true,
      });
      completeRegistration(
        completeRegistrationFormValues,
        selectedAddressParsed,
        history.push,
      );
    } else {
      touch(...Object.keys(formErrors));
      this.setState({ formSubmissionError: true }, () => {
        focusValidationSummaryById('register-validation-summary');
        setComponentTimeout(this.timeoutRef, () => {
          scrollToFirstErrorField(formErrors, this.formRef);
        }, VALIDATION_SUMMARY_TO_FIELD_DELAY_MS);
      });
    }
  }

  selectAddressFn(address) {
    const { selectAddress } = this.props;
    selectAddress({ address });
  }

  render() {
    const {
      selectedAddress,
      registrationSuccess,
      registrationFailed,
      errorDetail,
      statusCode,
      resetAutocomplete,
      addressErrorDetail,
      location,
      isFetching,
      address,
      guardChecking,
      guardReady,
    } = this.props;
    const { formSubmissionError, isSubmitting } = this.state;

    if (guardChecking || !guardReady) {
      return (
        <div className="complete-registration row justify-content-center">
          <div className="col-xl-7 col-lg-8 col-md-8 col-sm-10 loading-wrapper">
            <BounceLoader color="#084267" />
          </div>
        </div>
      );
    }

    return (
      <div key={location.pathname} className="complete-registration row justify-content-center">
        <div className="col-xl-7 col-lg-8 col-md-8 col-sm-10 ">
          <div className="requirements-info-accordion-wrapper">
            <RequirementsInfoAccordion />
          </div>
          <RegisterForm
            formRef={this.formRef}
            selectedAddress={selectedAddress}
            submitFormFn={this.handleRegisterSubmit}
            selectAddressFn={this.selectAddressFn}
            registrationSuccess={registrationSuccess}
            registrationFailed={registrationFailed}
            formSubmissionError={formSubmissionError}
            errorDetail={errorDetail}
            statusCode={statusCode}
            resetAutocomplete={resetAutocomplete}
            addressErrorDetail={addressErrorDetail}
            isFetching={isFetching || isSubmitting}
            address={address}
          />
        </div>
      </div>
    );
  }
}

export default reduxForm({
  form: formName,
  initialValues: {
    suffix: '',
    state: '',
  },
})(CompleteRegistrationView);
